/* Syba Klaviyo shield guard — neutralizes wedged onsite-form click shields.
 *
 * WHY (incident 2026-07-18)
 * Klaviyo served this store a broken canary of its onsite-forms runtime
 * (loader assetSource "build-preview/commit-d6606318…"). Two observed
 * failure modes on live, both verified with trusted (real) input:
 *
 *   1. WEDGED SHIELD — a popup gets "dismissed" in Klaviyo's state
 *      (localStorage disabledForms updated) but its DOM stays mounted:
 *      a fixed, full/large-viewport container with pointer-events:auto
 *      and no visible content. Every real click inside it dies silently.
 *      Symptoms reported by customers/merchant that day: navigation bar
 *      partially unclickable; a Kaching quantity-deal ("3x") click in the
 *      quick-add modal not registering, so ATC added 1 unit instead of 3.
 *
 *   2. DEAD CLOSE BUTTON — the visible "Soil Surprise" popup ignores
 *      clicks on its own close button (state records the close; the DOM
 *      never unmounts), leaving a fullscreen shield + body scroll-lock.
 *
 * WHAT THIS DOES
 *   A. Auto-neutralize invisible interceptors: a Klaviyo layer that is
 *      position:fixed, pointer-events:auto, covers ≥20% of the viewport
 *      AND has no visibly-rendered form content across TWO consecutive
 *      scans (≥2s apart, so fade-in animations are never touched) gets
 *      pointer-events:none. Reversible: if Klaviyo later renders visible
 *      content in that layer, the override is removed.
 *   B. Honor the user's close intent: when a real click lands on a
 *      Klaviyo close control and the form is still visibly mounted 1.2s
 *      later, force-hide that form's root and release the scroll lock.
 *      This only ever runs AFTER an explicit user close attempt, so a
 *      healthy popup (which unmounts itself immediately) is unaffected.
 *   C. Scroll-lock hygiene: drop Klaviyo's body scroll-lock class when no
 *      visible Klaviyo form exists (same two-scan debounce).
 *
 * WHAT THIS NEVER DOES
 *   - Never touches a layer containing a visible form (open popups keep
 *     working; signups are not suppressed).
 *   - Never removes Klaviyo nodes — only pointer-events / display:none on
 *     the wedged root, so Klaviyo's own runtime can still recover.
 *   - Never runs against the legacy BIS modal (different DOM namespace).
 *
 * Remove this file when Klaviyo's production runtime is confirmed fixed
 * (re-test: fresh incognito, trigger popup, close it, click through the
 * area it covered).
 */
(function () {
  'use strict';
  if (window.__sybaShieldGuard) return; // idempotent under double-include
  window.__sybaShieldGuard = true;

  var KL_ROOT_SELECTOR = '.kl-private-reset-css-Xuajs1';
  var SCAN_MS = 1500;
  var MIN_VIEWPORT_COVERAGE = 0.2; // 20% — catches form-box (≈32%) and fullscreen wedges, never the ~55px teaser
  var suspects = typeof WeakMap === 'function' ? new WeakMap() : null; // el -> consecutive qualifying scans
  if (!suspects) return; // ancient browser: do nothing rather than risk breakage

  // Lightweight introspection for QA (harmless in production): lets a test
  // assert the loop is alive instead of inferring from side effects.
  var stats = { scans: 0, candidates: 0, neutralized: 0, restored: 0, forcedCloses: 0, lastError: null };
  window.__sybaShieldGuardStats = stats;

  function hasVisibleFormContent(root) {
    try {
      var nodes = root.querySelectorAll('form.klaviyo-form, [role="dialog"]');
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var r = n.getBoundingClientRect();
        if (r.width < 10 || r.height < 10) continue;
        if (typeof n.checkVisibility === 'function') {
          if (n.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true })) return true;
        } else {
          var s = getComputedStyle(n);
          if (s.display !== 'none' && s.visibility !== 'hidden' && parseFloat(s.opacity) > 0.05) return true;
        }
      }
    } catch (e) { return true; } // on any doubt, treat as visible (never neutralize)
    return false;
  }

  function candidateRoots() {
    var out = [];
    try {
      var all = document.querySelectorAll(KL_ROOT_SELECTOR);
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        // Only consider shield-shaped layers: fixed, intercepting, large.
        var s = getComputedStyle(el);
        if (s.position !== 'fixed' || s.pointerEvents === 'none' || s.display === 'none') continue;
        var r = el.getBoundingClientRect();
        var cover = (r.width * r.height) / Math.max(1, window.innerWidth * window.innerHeight);
        if (cover < MIN_VIEWPORT_COVERAGE) continue;
        out.push(el);
      }
    } catch (e) { /* never break the page over the guard */ }
    return out;
  }

  function releaseScrollLockIfOrphaned(force) {
    try {
      if (!document.body.classList.contains('klaviyo-prevent-body-scrolling')) return;
      var anyVisible = false;
      var forms = document.querySelectorAll('form.klaviyo-form');
      for (var i = 0; i < forms.length; i++) {
        var r = forms[i].getBoundingClientRect();
        if (r.width > 10 && r.height > 10) { anyVisible = true; break; }
      }
      if (anyVisible) { window.__sybaShieldLockStrikes = 0; return; }
      // Debounce: require two consecutive orphaned scans (or an explicit force).
      window.__sybaShieldLockStrikes = (window.__sybaShieldLockStrikes || 0) + 1;
      if (force || window.__sybaShieldLockStrikes >= 2) {
        document.body.classList.remove('klaviyo-prevent-body-scrolling');
        window.__sybaShieldLockStrikes = 0;
      }
    } catch (e) { /* noop */ }
  }

  function scan() {
    stats.scans++;
    // Recovery pass FIRST, over previously-neutralized layers. This must be a
    // separate query: candidateRoots() excludes pointer-events:none elements,
    // which is precisely the state a neutralized layer is in.
    try {
      var healed = document.querySelectorAll(KL_ROOT_SELECTOR + '[data-syba-shield-neutralized="1"]');
      for (var h = 0; h < healed.length; h++) {
        // Klaviyo re-rendered visible content → hand control back.
        if (hasVisibleFormContent(healed[h])) {
          healed[h].style.removeProperty('pointer-events');
          healed[h].style.removeProperty('display');
          healed[h].removeAttribute('data-syba-shield-neutralized');
          suspects.delete(healed[h]);
          stats.restored++;
        }
      }
    } catch (e) { stats.lastError = String(e); }
    var roots = candidateRoots();
    stats.candidates = roots.length;
    for (var i = 0; i < roots.length; i++) {
      try {
        var el = roots[i];
        if (el.getAttribute('data-syba-shield-neutralized') === '1') continue; // handled by recovery pass
        if (hasVisibleFormContent(el)) { suspects.delete(el); continue; }
        var strikes = (suspects.get(el) || 0) + 1;
        suspects.set(el, strikes);
        if (strikes >= 2) { // ≥2 scans ≈ ≥1.5–3s in the wedged state — fade-ins are long gone
          el.style.setProperty('pointer-events', 'none', 'important');
          el.setAttribute('data-syba-shield-neutralized', '1');
          stats.neutralized++;
        }
      } catch (e) { stats.lastError = String(e); }
    }
    releaseScrollLockIfOrphaned(false);
  }

  // B: user clicked a Klaviyo close control — if the form survives, force it out.
  function onDocClick(ev) {
    try {
      var t = ev.target;
      if (!t || !t.closest) return;
      var closeBtn = t.closest('button.klaviyo-close-form, ' + KL_ROOT_SELECTOR + ' [aria-label*="close" i]');
      if (!closeBtn) return;
      var root = closeBtn.closest(KL_ROOT_SELECTOR);
      // Walk to the outermost Klaviyo wrapper attached to <body> so we hide the
      // backdrop too, not just the inner card.
      while (root && root.parentElement && root.parentElement !== document.body) root = root.parentElement;
      if (!root) return;
      setTimeout(function () {
        try {
          if (!root.isConnected) return; // healthy close — Klaviyo unmounted it
          if (!hasVisibleFormContent(root)) return; // already gone visually; scan handles leftovers
          root.style.setProperty('display', 'none', 'important');
          root.setAttribute('data-syba-shield-neutralized', '1');
          stats.forcedCloses++;
          releaseScrollLockIfOrphaned(true);
        } catch (e) { /* noop */ }
      }, window.__sybaShieldCloseDelayMs || 1200); // delay overridable by QA fixtures only
    } catch (e) { /* noop */ }
  }

  function start() {
    try {
      document.addEventListener('click', onDocClick, true);
      setInterval(scan, SCAN_MS);
      // Manual trigger for QA fixtures (timer throttling in hidden tabs makes
      // interval-based test timing unreliable) and for ops debugging.
      window.__sybaShieldGuardScan = scan;
    } catch (e) { /* noop */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
